import { useMemo, useState } from 'react';

type DiffChunk = {
    value: string;
    type: 'equal' | 'insert' | 'delete';
};

const isWhitespaceToken = (value: string) => value.trim().length === 0;

const tokenizeWords = (value: string) => value.match(/\S+|\s+/g) ?? [];

const buildDiffChunks = (beforeText: string, afterText: string): DiffChunk[] => {
    if (!beforeText && !afterText) {
        return [];
    }

    const beforeTokens = tokenizeWords(beforeText);
    const afterTokens = tokenizeWords(afterText);
    const beforeLength = beforeTokens.length;
    const afterLength = afterTokens.length;

    const lcsTable: number[][] = Array.from({ length: beforeLength + 1 }, () =>
        Array.from({ length: afterLength + 1 }, () => 0)
    );

    for (let beforeIndex = 1; beforeIndex <= beforeLength; beforeIndex += 1) {
        for (let afterIndex = 1; afterIndex <= afterLength; afterIndex += 1) {
            if (beforeTokens[beforeIndex - 1] === afterTokens[afterIndex - 1]) {
                lcsTable[beforeIndex][afterIndex] = lcsTable[beforeIndex - 1][afterIndex - 1] + 1;
                continue;
            }

            lcsTable[beforeIndex][afterIndex] = Math.max(
                lcsTable[beforeIndex - 1][afterIndex],
                lcsTable[beforeIndex][afterIndex - 1]
            );
        }
    }

    const chunks: DiffChunk[] = [];
    let beforeIndex = beforeLength;
    let afterIndex = afterLength;

    while (beforeIndex > 0 || afterIndex > 0) {
        if (
            beforeIndex > 0 &&
            afterIndex > 0 &&
            beforeTokens[beforeIndex - 1] === afterTokens[afterIndex - 1]
        ) {
            chunks.push({ value: beforeTokens[beforeIndex - 1], type: 'equal' });
            beforeIndex -= 1;
            afterIndex -= 1;
            continue;
        }

        if (
            afterIndex > 0 &&
            (beforeIndex === 0 || lcsTable[beforeIndex][afterIndex - 1] >= lcsTable[beforeIndex - 1][afterIndex])
        ) {
            chunks.push({ value: afterTokens[afterIndex - 1], type: 'insert' });
            afterIndex -= 1;
            continue;
        }

        chunks.push({ value: beforeTokens[beforeIndex - 1], type: 'delete' });
        beforeIndex -= 1;
    }

    return chunks.reverse().reduce<DiffChunk[]>((accumulator, chunk) => {
        const lastChunk = accumulator[accumulator.length - 1];
        if (
            lastChunk &&
            lastChunk.type === chunk.type &&
            isWhitespaceToken(lastChunk.value) === isWhitespaceToken(chunk.value)
        ) {
            lastChunk.value += chunk.value;
            return accumulator;
        }

        accumulator.push({ ...chunk });
        return accumulator;
    }, []);
};

const chunkClassNames: Record<DiffChunk['type'], string> = {
    equal: 'text-gray-900',
    insert: 'bg-green-200 text-green-900',
    delete: 'bg-red-200 text-red-900',
};

const formatWhitespaceForDisplay = (value: string) => value.replace(/\t/g, '\\t\t').replace(/\n/g, '\\n\n');

export const DiffTool = () => {
    const [beforeText, setBeforeText] = useState('');
    const [afterText, setAfterText] = useState('');

    const diffChunks = useMemo(() => buildDiffChunks(beforeText, afterText), [beforeText, afterText]);
    const hasInput = beforeText.trim().length > 0 || afterText.trim().length > 0;

    const handleBeforeChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setBeforeText(event.target.value);
    };

    const handleAfterChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setAfterText(event.target.value);
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Diff Checker</h2>
            <p className="text-sm text-gray-600 mb-4">
                Highlight added and removed words. Whitespace blocks are shaded; newlines show as \n.
            </p>
            <div className="grid gap-4 md:grid-cols-2">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="diff-before">
                        Original
                    </label>
                    <textarea
                        id="diff-before"
                        className="w-full min-h-[120px] rounded-md border border-gray-300 p-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        placeholder="Paste original text..."
                        value={beforeText}
                        onChange={handleBeforeChange}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="diff-after">
                        Updated
                    </label>
                    <textarea
                        id="diff-after"
                        className="w-full min-h-[120px] rounded-md border border-gray-300 p-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        placeholder="Paste updated text..."
                        value={afterText}
                        onChange={handleAfterChange}
                    />
                </div>
            </div>
            <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Diff Output</label>
                <div className="min-h-[120px] rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900 whitespace-pre-wrap break-words">
                    {hasInput ? (
                        diffChunks.map((chunk, index) => {
                            const isWhitespace = isWhitespaceToken(chunk.value);
                            const className = [
                                chunkClassNames[chunk.type],
                                isWhitespace ? 'bg-gray-200 text-gray-700 border border-dashed border-gray-400' : '',
                            ]
                                .filter(Boolean)
                                .join(' ');

                            return (
                                <span key={`${chunk.type}-${index}`} className={className}>
                                    {isWhitespace ? formatWhitespaceForDisplay(chunk.value) : chunk.value}
                                </span>
                            );
                        })
                    ) : (
                        <span className="text-gray-400">Enter text to see the diff.</span>
                    )}
                </div>
            </div>
        </div>
    );
};
